/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';
import { CLOSE_NEGOTIABLE_QUOTE_MUTATION } from './graphql/CloseNegotiableQuoteMutation';

export interface CloseNegotiableQuoteInput {
  quoteUids: string[];
}

export interface CloseNegotiableQuoteSuccessResult {
  __typename: 'NegotiableQuoteUidOperationSuccess';
  quoteUid: string;
}

export interface CloseNegotiableQuoteFailureError {
  __typename: string;
  message?: string;
  uid?: string;
}

export interface CloseNegotiableQuoteFailureResult {
  __typename: 'CloseNegotiableQuoteOperationFailure';
  quoteUid: string;
  errors: CloseNegotiableQuoteFailureError[];
}

export interface CloseNegotiableQuoteResult {
  resultStatus: string;
  operationResults: Array<CloseNegotiableQuoteSuccessResult | CloseNegotiableQuoteFailureResult>;
}

export const closeNegotiableQuote = async (input: CloseNegotiableQuoteInput): Promise<CloseNegotiableQuoteResult> => { 
  if (!state.authenticated) {
    return Promise.reject(new Error('Unauthorized'));
  }

  const { quoteUids } = input;

  if (!quoteUids || quoteUids.length === 0) {
    throw new Error('Quote UIDs are required');
  }

  try {
    const response: any = await fetchGraphQl(CLOSE_NEGOTIABLE_QUOTE_MUTATION, {
      variables: {
        quoteUids,
      },
    });

    const { errors } = response || {};
    if (errors && errors.length) {
      const errorMessages = errors
        .map((e: any) => e?.message)
        .filter(Boolean)
        .join('; ');
      throw new Error(errorMessages || 'Failed to close negotiable quotes');
    }

    const raw = response?.data?.closeNegotiableQuotes;
    if (!raw) {
      throw new Error('No close result returned');
    }

    const transformed: CloseNegotiableQuoteResult = {
      resultStatus: raw.result_status,
      operationResults: (raw.operation_results || []).map((result: any) => {
        const typename = result?.__typename;
        if (typename === 'NegotiableQuoteUidOperationSuccess') {
          const success: CloseNegotiableQuoteSuccessResult = {
            __typename: 'NegotiableQuoteUidOperationSuccess',
            quoteUid: result?.quote_uid,
          };
          return success;
        }
        const failure: CloseNegotiableQuoteFailureResult = {
          __typename: 'CloseNegotiableQuoteOperationFailure',
          quoteUid: result?.quote_uid,
          errors: (result?.errors || []).map((err: any) => ({
            __typename: err?.__typename,
            message: err?.message,
            uid: err?.uid,
          })),
        };
        return failure;
      }),
    };

    // Check for operation failures
    const failures = transformed.operationResults
      .filter(
        (res) => res.__typename === 'CloseNegotiableQuoteOperationFailure'
      )
      .map((res) => res as CloseNegotiableQuoteFailureResult);

    if (failures.length > 0) {
      // Create error message from failures
      const failureMessages = failures
        .map((failure) => {
          if (failure.errors && failure.errors.length > 0) {
            return failure.errors
              .map(
                (err) =>
                  err.message || `Failed to close quote ${failure.quoteUid}`
              )
              .join(', ');
          }
          return `Failed to close quote ${failure.quoteUid}`;
        })
        .join('; ');

      throw new Error(failureMessages);
    }

    // Emit success event with closed quote UIDs
    const closedUids = transformed.operationResults
      .filter((res) => res.__typename === 'NegotiableQuoteUidOperationSuccess')
      .map((res) => (res as CloseNegotiableQuoteSuccessResult).quoteUid);

    if (closedUids.length > 0) {
      events.emit('quote-management/negotiable-quote-closed', {
        closedQuoteUids: closedUids,
        resultStatus: transformed.resultStatus,
      });
    }

    return transformed;
  } catch (error) {
    // Emit error event for integration layer
    events.emit('quote-management/negotiable-quote-close-error', {
      error: error instanceof Error ? error : new Error(String(error)),
      attemptedQuoteUids: quoteUids,
    });
    return Promise.reject(error);
  }
}
